import { ReactNode } from "react";
import type { WorkDateField, WorkRoleField } from "@api/workItem.api";
import { Entity } from "@common/types/entity";
import { TransactionReviewField } from "@common/types/transactionFieldReview";
import type { CheckboxIndicatorState } from "@design-system/Checkbox/CheckboxIndicator";
import { ClassWithChildren } from "@helpers/class";

export interface InjectedCommandOption {
    id: string;
    label: string;
    sublabel?: string;
    icon?: ReactNode;
    iconClassName?: string;
    keywords?: string[];
    disabled?: boolean;
    groupHeader?: string;
    className?: string;
    /** Set when selecting opens its own modal, so the palette can't stay open. */
    shouldClosePalette?: boolean;
    onSelect: () => void;
}

export type ScheduleApplicationBulkLifecycleOption = Pick<
    InjectedCommandOption,
    "id" | "label" | "keywords" | "onSelect"
> & { disabledReason: string | undefined };

export interface EntityBulkCommandOption {
    entity: Entity;
    checkboxState: CheckboxIndicatorState;
}

export interface TaskEntityBulkOperation {
    entityId: number;
    operation: "add" | "remove";
}

/** A work date step: the calendar's day and bounds, and what picking a day does. */
export interface ClientsWorkDateStep {
    value?: string;
    minDate?: string;
    maxDate?: string;
    onSelect: (date: string) => void;
}

/**
 * What Cmd+K offers for the Work items it acts on: the checked ones, or the
 * open one. Edits and Cancel are left out when none of them is open work,
 * and a role when none of them has open tasks for it.
 */
export interface ClientsWorkCommandOptions {
    heading: string;
    memberOptions?: Partial<Record<WorkRoleField, InjectedCommandOption[]>>;
    dateSteps?: Record<WorkDateField, ClientsWorkDateStep>;
    onViewInActions?: () => void;
    onOpenClient?: () => void;
    onCancel?: () => void;
    onDelete: () => void;
}

export interface CommandsOptions {
    /** Replaces the opened directory's scope chip, e.g. with the one record it acts on. */
    scopeLabel?: string;
    /** Lists the global commands below the opened directory's own. */
    includeGlobalCommands?: boolean;
    defaultEntity?: Entity;
    /**
     * Set when the Add accounts list is opened from the organization view: the
     * firm connects without a client, so the connect flows skip the workspace
     * post-connect steps and rows that cannot land in the org integrations
     * table are hidden.
     */
    organizationId?: string;
    taskBulkStatusOptions?: InjectedCommandOption[];
    taskBulkMemberOptions?: InjectedCommandOption[];
    taskBulkPriorityOptions?: InjectedCommandOption[];
    taskBulkTaskGroupOptions?: InjectedCommandOption[];
    taskBulkEntityOptions?: EntityBulkCommandOption[];
    /** Fires once with the staged add/remove edits — the whole batch is applied atomically. */
    taskBulkApplyEntities?: (operations: TaskEntityBulkOperation[]) => void;
    /** Selected tasks whose type can't change entity (e.g. transactions-based tasks) — excluded from taskBulkEntityOptions. */
    taskBulkEntityLockedCount?: number;
    onTaskBulkDelete?: () => void;
    selectedTaskCount?: number;
    closeTemplateTaskBulkDeadlineOptions?: InjectedCommandOption[];
    closeTemplateTaskBulkRoleOptions?: InjectedCommandOption[];
    closeTemplateTaskBulkStageOptions?: InjectedCommandOption[];
    closeTemplateTaskBulkEstimateOptions?: InjectedCommandOption[];
    onCloseTemplateTaskBulkDelete?: () => void;
    selectedCloseTemplateTaskCount?: number;
    documentBulkEntityOptions?: InjectedCommandOption[];
    documentBulkAccountOptions?: InjectedCommandOption[];
    documentBulkCategoryOptions?: InjectedCommandOption[];
    isDocumentBulkFieldLocked?: boolean;
    documentBulkFieldLockedTooltip?: string;
    onDocumentBulkRemove?: () => void;
    onDocumentBulkUnmatch?: () => void;
    onDocumentBulkAddToImporter?: () => void;
    onDocumentBulkCreateTask?: () => void;
    onDocumentBulkRevertRename?: () => void;
    selectedDocumentCount?: number;
    transactionBulkEntityOptions?: InjectedCommandOption[];
    transactionBulkCounterpartyOptions?: InjectedCommandOption[];
    transactionBulkCustomerOptions?: InjectedCommandOption[];
    transactionBulkCategoryOptions?: InjectedCommandOption[];
    transactionBulkCoaOptions?: InjectedCommandOption[];
    transactionBulkCoaHeading?: string;
    transactionBulkAccrualCoaOptions?: InjectedCommandOption[];
    transactionBulkScheduleOptions?: InjectedCommandOption[];
    transactionBulkClassTree?: ClassWithChildren[];
    transactionBulkClassesMap?: Map<string, ClassWithChildren>;
    transactionApplyClasses?: (classIds: string[]) => void;
    isTransactionBulkFieldLocked?: boolean;
    transactionBulkFieldLockedTooltip?: string;
    isTransactionBulkUnsplitLocked?: boolean;
    transactionBulkUnsplitLockedTooltip?: string;
    onTransactionBulkUnmatchAll?: () => void;
    onTransactionBulkMatch?: () => Promise<void>;
    onTransactionBulkUnmatchInvoices?: () => Promise<void>;
    onTransactionBulkAcceptInvoiceMatches?: () => Promise<void>;
    onTransactionBulkAcceptBillMatches?: () => Promise<void>;
    onTransactionBulkUnsplitAll?: () => void;
    onTransactionBulkCopyLink?: () => void;
    onTransactionBulkCreateTask?: () => void;
    onTransactionBulkRevertGL?: () => void;
    onTransactionBulkRemoveCounterparty?: () => void;
    onTransactionBulkRemoveCustomer?: () => void;
    onTransactionBulkInvoiceToCustomer?: () => void;
    onTransactionBulkRemoveClasses?: () => Promise<void>;
    onTransactionBulkDelete?: () => void;
    onTransactionBulkReconcileGusto?: () => void;
    onTransactionBulkSetMemo?: () => void;
    onTransactionBulkRemoveMemo?: () => Promise<void>;
    onTransactionBulkMarkReviewedForFields?: (
        fields: TransactionReviewField[],
    ) => void;
    onTransactionBulkUnreviewForFields?: (
        fields: TransactionReviewField[],
    ) => void;
    onTransactionBulkExclude1099?: () => Promise<void>;
    onTransactionBulkInclude1099?: () => Promise<void>;
    selectedTransactionCount?: number;
    /** Snapshot from the table provider; the palette cannot read selection context. */
    hasExistingClassAssignments?: boolean;
    transactionsWithExistingClassCount?: number;
    /**
     * Count of selected transactions matched to an invoice or bill — their
     * class / counterparty / cash ledger / accrual ledger are locked
     * server-side (MatchedTransactionFieldLockedError). 0 when none are
     * locked. Used to hide those bulk commands entirely when every selected
     * transaction is locked, and to append a "(N can't be changed...)"
     * suffix to the heading when the selection is mixed — see
     * partitionSelectionByInvoiceOrBillLock /
     * buildInvoiceOrBillLockedSuffix.
     */
    transactionsLockedByInvoiceOrBillCount?: number;
    /**
     * Count of selected transactions whose Accrual ledger a live recognition
     * schedule owns (ScheduledTransactionAccrualLedgerLockedError). Only the
     * Set accrual ledger command excludes them and shows the suffix — see
     * partitionSelectionByScheduleLock / buildScheduleLockedSuffix.
     */
    transactionsLockedByScheduleCount?: number;
    /**
     * Count of selected transactions a live recognition schedule keeps in
     * their entity (ScheduledTransactionEntityLockedError). Set entity skips
     * them and shows the suffix — see partitionSelectionByScheduleEntityLock.
     */
    transactionsLockedByScheduleEntityCount?: number;
    applyTemplateEntityBulkPreparerOptions?: InjectedCommandOption[];
    applyTemplateEntityBulkReviewerOptions?: InjectedCommandOption[];
    applyTemplateEntityBulkManagerOptions?: InjectedCommandOption[];
    selectedApplyTemplateEntityCount?: number;
    onClientBulkMemo?: () => void;
    onClientBulkTeam?: () => void;
    onClientBulkServices?: () => void;
    selectedClientCount?: number;
    onClientTasksBulkMemo?: () => void;
    onClientTasksBulkPriority?: () => void;
    onClientTasksBulkStage?: () => void;
    onClientTasksBulkCloseDate?: () => void;
    onClientTasksBulkDueDate?: () => void;
    onClientTasksBulkServices?: () => void;
    onClientTasksBulkAssignee?: () => void;
    selectedClientTasksCount?: number;
    clientsWork?: ClientsWorkCommandOptions;
    onOrgIntegrationBulkSendEmail?: () => void;
    onOrgIntegrationBulkClearSelection?: () => void;
    /**
     * Left out while the action is unavailable — a mixed-client selection can't
     * take one entity — so the command is hidden rather than shown dead.
     */
    onOrgIntegrationBulkUpdateClient?: () => void;
    onOrgIntegrationBulkUpdateEntity?: () => void;
    /** Selected rows, which are accounts — not the number of emails sent. */
    selectedOrgIntegrationAccountCount?: number;
    /** Eligible accounts; 0 hides the send command rather than offering a no-op. */
    eligibleOrgIntegrationReminderCount?: number;
    /** Selected accounts still waiting for a client; 0 hides the client update. */
    eligibleOrgIntegrationClientCount?: number;
    /** Selected accounts whose entity can change; 0 hides the entity update. */
    eligibleOrgIntegrationEntityCount?: number;
    lineItemsApplyClasses?: (classIds: string[]) => void;
    onLineItemsBulkEdit?: () => void;
    onLineItemsBulkCreateTask?: () => void;
    lineItemsBulkAccrualCoaOptions?: InjectedCommandOption[];
    lineItemsBulkReceivablesOptions?: InjectedCommandOption[];
    lineItemsBulkPayablesOptions?: InjectedCommandOption[];
    lineItemsSelectionLabel?: string;
    selectedLineItemCount?: number;
    hasSelectedAllMatchingLineItems?: boolean;
    catalogItemBulkClassTree?: ClassWithChildren[];
    catalogItemBulkClassesMap?: Map<string, ClassWithChildren>;
    catalogItemApplyClasses?: (classIds: string[]) => void;
    catalogItemBulkIncomeAccountOptions?: InjectedCommandOption[];
    catalogItemBulkReceivablesAccountOptions?: InjectedCommandOption[];
    selectedCatalogItemCount?: number;
    catalogItemBulkArchiveCount?: number;
    catalogItemBulkEnableCount?: number;
    onCatalogItemBulkArchive?: () => void;
    onCatalogItemBulkEnable?: () => void;
    selectedCounterpartyCount?: number;
    onCounterpartyBulkMark1099?: () => Promise<void>;
    onCounterpartyBulkUnmark1099?: () => Promise<void>;
    onCounterpartyBulkDelete?: () => void;
    onCounterpartyBulkCreateTask?: () => void;
    selectedBillCount?: number;
    onBillBulkCreateTask?: () => void;
    onOccurrenceBulkApply?: () => void;
    onOccurrenceBulkSkip?: () => void;
    onOccurrenceBulkUnskip?: () => void;
    onOccurrenceBulkDelete?: () => void;
    occurrenceBulkPreparerOptions?: InjectedCommandOption[];
    occurrenceBulkReviewerOptions?: InjectedCommandOption[];
    occurrenceBulkManagerOptions?: InjectedCommandOption[];
    occurrenceBulkClientOptions?: InjectedCommandOption[];
    selectedOccurrenceCount?: number;
    onScheduleBulkPause?: () => void;
    onScheduleBulkResume?: () => void;
    onScheduleBulkDelete?: () => void;
    selectedScheduleCount?: number;
    selectedScheduleApplicationCount?: number;
    onScheduleApplicationsOpenRollforward?: () => void;
    onScheduleApplicationsOpenWaterfall?: () => void;
    onScheduleApplicationsMarkBilled?: () => void;
    scheduleApplicationsMarkBilledDisabledReason?: string;
    selectedScheduleMilestoneCount?: number;
    onScheduleApplicationsMilestoneAchieved?: () => void;
    scheduleApplicationsMilestoneAchievedDisabledReason?: string;
    scheduleApplicationsLifecycleActions?: ScheduleApplicationBulkLifecycleOption[];
    scheduleApplicationsChangePolicyOptions?: InjectedCommandOption[];
    scheduleApplicationsChangePolicyDisabledReason?: string;
    scheduleApplicationsChangePolicySelectedCount?: number;
    onCloseTemplateBulkArchive?: () => void;
    onCloseTemplateBulkUnarchive?: () => void;
    onCloseTemplateBulkDelete?: () => void;
    selectedCloseTemplateCount?: number;
    onInvoiceTemplateBulkArchive?: () => void;
    onInvoiceTemplateBulkUnarchive?: () => void;
    selectedInvoiceTemplateCount?: number;
    onOrganizationTaskTemplateBulkArchive?: () => void;
    onOrganizationTaskTemplateBulkEnable?: () => void;
    onOrganizationTaskTemplateBulkDetach?: () => void;
    onOrganizationTaskTemplateBulkDelete?: () => void;
    selectedOrganizationTaskTemplateCount?: number;
    onInvoiceBulkMarkVoid?: () => void;
    onInvoiceBulkMarkUncollectible?: () => void;
    invoiceBulkCounterpartyOptions?: InjectedCommandOption[];
    invoiceBulkScheduleOptions?: InjectedCommandOption[];
    /**
     * False when the selection contains any non-Draft invoice. Set
     * counterparty is all-or-nothing (see useInvoiceBulkActions), so the
     * command stays visible with an explanatory disabled tooltip.
     */
    canInvoiceBulkChangeCounterparty?: boolean;
    onInvoiceBulkDownloadPdf?: () => void;
    /** False when none of the selected invoices were created in Kick, or a download was just queued. */
    canInvoiceBulkDownloadPdf?: boolean;
    invoiceBulkDownloadPdfDisabledTooltip?: string;
    onInvoiceBulkDelete?: () => void;
    onInvoiceBulkCreateTask?: () => void;
    /** False when no selected invoice is a deletable draft. */
    canInvoiceBulkDelete?: boolean;
    selectedInvoiceCount?: number;
    onDocumentRetrievalBulkRemoveFromTask?: () => void;
    onDocumentRetrievalBulkStatementUnavailable?: () => void;
    onDocumentRetrievalBulkAskAbout?: () => void;
    selectedDocumentRetrievalCount?: number;
    onGetDocumentsBulkRemoveFromTask?: () => void;
    onGetDocumentsBulkAskAbout?: () => void;
    selectedGetDocumentsCount?: number;
    onAccrualDraftBulkConfirm?: () => void;
    accrualDraftBulkConfirmLabel?: string;
    onAccrualDraftBulkDiscard?: () => void;
    onAccrualDraftBulkRerun?: () => void;
    onAccrualDraftBulkMove?: () => void;
    accrualDraftBulkMoveLabel?: string;
    onAccrualDraftBulkConfirmCounterparties?: () => void;
    accrualDraftCounterpartyLabel?: "vendors" | "customers";
    selectedAccrualDraftCount?: number;
}

export interface CommandsListProps {
    options?: CommandsOptions;
}

export enum CommandsDirectory {
    HOME = "HOME",
    ACCOUNTS = "ACCOUNTS",
    ONBOARDING = "ONBOARDING",
    WORKSPACES = "WORKSPACES",
    TASK_BULK = "TASK_BULK",
    TASK_BULK_STATUS = "TASK_BULK_STATUS",
    TASK_BULK_MEMBER = "TASK_BULK_MEMBER",
    TASK_BULK_PRIORITY = "TASK_BULK_PRIORITY",
    TASK_BULK_TASK_GROUP = "TASK_BULK_TASK_GROUP",
    TASK_BULK_ENTITY = "TASK_BULK_ENTITY",
    DOCUMENT_BULK = "DOCUMENT_BULK",
    DOCUMENT_BULK_ENTITY = "DOCUMENT_BULK_ENTITY",
    DOCUMENT_BULK_ACCOUNT = "DOCUMENT_BULK_ACCOUNT",
    DOCUMENT_BULK_CATEGORY = "DOCUMENT_BULK_CATEGORY",
    TRANSACTION_BULK = "TRANSACTION_BULK",
    TRANSACTION_BULK_ENTITY = "TRANSACTION_BULK_ENTITY",
    TRANSACTION_BULK_COUNTERPARTY = "TRANSACTION_BULK_COUNTERPARTY",
    TRANSACTION_BULK_CUSTOMER = "TRANSACTION_BULK_CUSTOMER",
    TRANSACTION_BULK_CATEGORY = "TRANSACTION_BULK_CATEGORY",
    TRANSACTION_BULK_COA = "TRANSACTION_BULK_COA",
    TRANSACTION_BULK_ACCRUAL_COA = "TRANSACTION_BULK_ACCRUAL_COA",
    TRANSACTION_BULK_SCHEDULE = "TRANSACTION_BULK_SCHEDULE",
    TRANSACTION_BULK_CLASS = "TRANSACTION_BULK_CLASS",
    TRANSACTION_BULK_MARK_REVIEWED = "TRANSACTION_BULK_MARK_REVIEWED",
    TRANSACTION_BULK_UNREVIEW = "TRANSACTION_BULK_UNREVIEW",
    CLOSE_TEMPLATE_TASK_BULK = "CLOSE_TEMPLATE_TASK_BULK",
    CLOSE_TEMPLATE_TASK_BULK_DEADLINE = "CLOSE_TEMPLATE_TASK_BULK_DEADLINE",
    CLOSE_TEMPLATE_TASK_BULK_ROLE = "CLOSE_TEMPLATE_TASK_BULK_ROLE",
    CLOSE_TEMPLATE_TASK_BULK_STAGE = "CLOSE_TEMPLATE_TASK_BULK_STAGE",
    CLOSE_TEMPLATE_TASK_BULK_ESTIMATE = "CLOSE_TEMPLATE_TASK_BULK_ESTIMATE",
    APPLY_TEMPLATE_ENTITY_BULK = "APPLY_TEMPLATE_ENTITY_BULK",
    APPLY_TEMPLATE_ENTITY_BULK_PREPARER = "APPLY_TEMPLATE_ENTITY_BULK_PREPARER",
    APPLY_TEMPLATE_ENTITY_BULK_REVIEWER = "APPLY_TEMPLATE_ENTITY_BULK_REVIEWER",
    APPLY_TEMPLATE_ENTITY_BULK_MANAGER = "APPLY_TEMPLATE_ENTITY_BULK_MANAGER",
    CLIENT_BULK = "CLIENT_BULK",
    CLIENTS_WORK_BULK = "CLIENTS_WORK_BULK",
    CLIENTS_WORK_BULK_TEAM = "CLIENTS_WORK_BULK_TEAM",
    CLIENTS_WORK_BULK_PREPARER = "CLIENTS_WORK_BULK_PREPARER",
    CLIENTS_WORK_BULK_REVIEWER = "CLIENTS_WORK_BULK_REVIEWER",
    CLIENTS_WORK_BULK_MANAGER = "CLIENTS_WORK_BULK_MANAGER",
    CLIENTS_WORK_BULK_WORK_START = "CLIENTS_WORK_BULK_WORK_START",
    CLIENTS_WORK_BULK_WORK_END = "CLIENTS_WORK_BULK_WORK_END",
    LINE_ITEMS_BULK = "LINE_ITEMS_BULK",
    LINE_ITEMS_BULK_ACCRUAL_COA = "LINE_ITEMS_BULK_ACCRUAL_COA",
    LINE_ITEMS_BULK_RECEIVABLES = "LINE_ITEMS_BULK_RECEIVABLES",
    LINE_ITEMS_BULK_PAYABLES = "LINE_ITEMS_BULK_PAYABLES",
    LINE_ITEMS_BULK_CLASS = "LINE_ITEMS_BULK_CLASS",
    CATALOG_ITEM_BULK = "CATALOG_ITEM_BULK",
    CATALOG_ITEM_BULK_CLASS = "CATALOG_ITEM_BULK_CLASS",
    CATALOG_ITEM_BULK_INCOME_ACCOUNT = "CATALOG_ITEM_BULK_INCOME_ACCOUNT",
    CATALOG_ITEM_BULK_RECEIVABLES_ACCOUNT = "CATALOG_ITEM_BULK_RECEIVABLES_ACCOUNT",
    ACCRUAL_DRAFT_BULK = "ACCRUAL_DRAFT_BULK",
    ACCRUAL_REVIEW_BULK = "ACCRUAL_REVIEW_BULK",
    COUNTERPARTY_BULK = "COUNTERPARTY_BULK",
    BILL_BULK = "BILL_BULK",
    OCCURRENCE_BULK = "OCCURRENCE_BULK",
    OCCURRENCE_BULK_PREPARER = "OCCURRENCE_BULK_PREPARER",
    OCCURRENCE_BULK_REVIEWER = "OCCURRENCE_BULK_REVIEWER",
    OCCURRENCE_BULK_MANAGER = "OCCURRENCE_BULK_MANAGER",
    OCCURRENCE_BULK_CLIENT = "OCCURRENCE_BULK_CLIENT",
    SCHEDULE_BULK = "SCHEDULE_BULK",
    SCHEDULE_APPLICATION_BULK = "SCHEDULE_APPLICATION_BULK",
    SCHEDULE_APPLICATION_BULK_CHANGE_POLICY = "SCHEDULE_APPLICATION_BULK_CHANGE_POLICY",
    CLOSE_TEMPLATE_BULK = "CLOSE_TEMPLATE_BULK",
    ORG_INTEGRATION_BULK = "ORG_INTEGRATION_BULK",
    INVOICE_TEMPLATE_BULK = "INVOICE_TEMPLATE_BULK",
    ORGANIZATION_TASK_TEMPLATE_BULK = "ORGANIZATION_TASK_TEMPLATE_BULK",
    INVOICE_BULK = "INVOICE_BULK",
    INVOICE_BULK_COUNTERPARTY = "INVOICE_BULK_COUNTERPARTY",
    INVOICE_BULK_SCHEDULE = "INVOICE_BULK_SCHEDULE",
    DOCUMENT_RETRIEVAL_BULK = "DOCUMENT_RETRIEVAL_BULK",
    GET_DOCUMENTS_BULK = "GET_DOCUMENTS_BULK",
    AGENT_RUN_CLOSE = "AGENT_RUN_CLOSE",
}

export const COMMAND_DIRECTORY_LABELS = {
    [CommandsDirectory.HOME]: "Home",
    [CommandsDirectory.ACCOUNTS]: "Add accounts",
    [CommandsDirectory.ONBOARDING]: "Add accounts",
    [CommandsDirectory.WORKSPACES]: "Workspaces",
    [CommandsDirectory.TASK_BULK]: "Bulk actions",
    [CommandsDirectory.TASK_BULK_STATUS]: "Set status",
    [CommandsDirectory.TASK_BULK_MEMBER]: "Assign member",
    [CommandsDirectory.TASK_BULK_PRIORITY]: "Set priority",
    [CommandsDirectory.TASK_BULK_TASK_GROUP]: "Set task group",
    [CommandsDirectory.TASK_BULK_ENTITY]: "Set entities",
    [CommandsDirectory.DOCUMENT_BULK]: "Bulk actions",
    [CommandsDirectory.DOCUMENT_BULK_ENTITY]: "Set entity",
    [CommandsDirectory.DOCUMENT_BULK_ACCOUNT]: "Set account",
    [CommandsDirectory.DOCUMENT_BULK_CATEGORY]: "Set category",
    [CommandsDirectory.TRANSACTION_BULK]: "Bulk actions",
    [CommandsDirectory.TRANSACTION_BULK_ENTITY]: "Set entity",
    [CommandsDirectory.TRANSACTION_BULK_COUNTERPARTY]: "Set counterparty",
    [CommandsDirectory.TRANSACTION_BULK_CUSTOMER]: "Set billable customer",
    [CommandsDirectory.TRANSACTION_BULK_CATEGORY]: "Set category",
    [CommandsDirectory.TRANSACTION_BULK_COA]: "Set cash ledger",
    [CommandsDirectory.TRANSACTION_BULK_ACCRUAL_COA]: "Set accrual ledger",
    [CommandsDirectory.TRANSACTION_BULK_SCHEDULE]: "Set recognition schedule",
    [CommandsDirectory.TRANSACTION_BULK_CLASS]: "Set class",
    [CommandsDirectory.TRANSACTION_BULK_MARK_REVIEWED]: "Mark reviewed",
    [CommandsDirectory.TRANSACTION_BULK_UNREVIEW]: "Mark unreviewed",
    [CommandsDirectory.CLOSE_TEMPLATE_TASK_BULK]: "Bulk actions",
    [CommandsDirectory.CLOSE_TEMPLATE_TASK_BULK_DEADLINE]: "Set deadline",
    [CommandsDirectory.CLOSE_TEMPLATE_TASK_BULK_ROLE]: "Set role",
    [CommandsDirectory.CLOSE_TEMPLATE_TASK_BULK_STAGE]: "Set stage",
    [CommandsDirectory.CLOSE_TEMPLATE_TASK_BULK_ESTIMATE]: "Set estimate",
    [CommandsDirectory.APPLY_TEMPLATE_ENTITY_BULK]: "Bulk actions",
    [CommandsDirectory.APPLY_TEMPLATE_ENTITY_BULK_PREPARER]: "Set preparer",
    [CommandsDirectory.APPLY_TEMPLATE_ENTITY_BULK_REVIEWER]: "Set reviewer",
    [CommandsDirectory.APPLY_TEMPLATE_ENTITY_BULK_MANAGER]: "Set manager",
    [CommandsDirectory.CLIENT_BULK]: "Bulk actions",
    [CommandsDirectory.CLIENTS_WORK_BULK]: "Bulk actions",
    [CommandsDirectory.CLIENTS_WORK_BULK_TEAM]: "Update engagement team",
    [CommandsDirectory.CLIENTS_WORK_BULK_PREPARER]: "Preparer",
    [CommandsDirectory.CLIENTS_WORK_BULK_REVIEWER]: "Reviewer",
    [CommandsDirectory.CLIENTS_WORK_BULK_MANAGER]: "Manager",
    [CommandsDirectory.CLIENTS_WORK_BULK_WORK_START]: "Update work start",
    [CommandsDirectory.CLIENTS_WORK_BULK_WORK_END]: "Update work end",
    [CommandsDirectory.LINE_ITEMS_BULK]: "Bulk actions",
    [CommandsDirectory.LINE_ITEMS_BULK_ACCRUAL_COA]: "Set accrual ledger",
    [CommandsDirectory.LINE_ITEMS_BULK_RECEIVABLES]: "Set receivables account",
    [CommandsDirectory.LINE_ITEMS_BULK_PAYABLES]: "Set payables account",
    [CommandsDirectory.LINE_ITEMS_BULK_CLASS]: "Set class",
    [CommandsDirectory.CATALOG_ITEM_BULK]: "Bulk actions",
    [CommandsDirectory.CATALOG_ITEM_BULK_CLASS]: "Set class",
    [CommandsDirectory.CATALOG_ITEM_BULK_INCOME_ACCOUNT]: "Set income account",
    [CommandsDirectory.CATALOG_ITEM_BULK_RECEIVABLES_ACCOUNT]:
        "Set receivables account",
    [CommandsDirectory.ACCRUAL_DRAFT_BULK]: "Bulk actions",
    [CommandsDirectory.ACCRUAL_REVIEW_BULK]: "Bulk actions",
    [CommandsDirectory.COUNTERPARTY_BULK]: "Bulk actions",
    [CommandsDirectory.BILL_BULK]: "Bulk actions",
    [CommandsDirectory.OCCURRENCE_BULK]: "Bulk actions",
    [CommandsDirectory.OCCURRENCE_BULK_PREPARER]: "Set preparer",
    [CommandsDirectory.OCCURRENCE_BULK_REVIEWER]: "Set reviewer",
    [CommandsDirectory.OCCURRENCE_BULK_MANAGER]: "Set manager",
    [CommandsDirectory.OCCURRENCE_BULK_CLIENT]: "Set client",
    [CommandsDirectory.SCHEDULE_BULK]: "Bulk actions",
    [CommandsDirectory.SCHEDULE_APPLICATION_BULK]: "Bulk actions",
    [CommandsDirectory.SCHEDULE_APPLICATION_BULK_CHANGE_POLICY]:
        "Change policy",
    [CommandsDirectory.CLOSE_TEMPLATE_BULK]: "Bulk actions",
    [CommandsDirectory.ORG_INTEGRATION_BULK]: "Bulk actions",
    [CommandsDirectory.INVOICE_TEMPLATE_BULK]: "Bulk actions",
    [CommandsDirectory.ORGANIZATION_TASK_TEMPLATE_BULK]: "Bulk actions",
    [CommandsDirectory.INVOICE_BULK]: "Bulk actions",
    [CommandsDirectory.INVOICE_BULK_COUNTERPARTY]: "Set counterparty",
    [CommandsDirectory.INVOICE_BULK_SCHEDULE]: "Set recognition schedule",
    [CommandsDirectory.DOCUMENT_RETRIEVAL_BULK]: "Bulk actions",
    [CommandsDirectory.GET_DOCUMENTS_BULK]: "Bulk actions",
    [CommandsDirectory.AGENT_RUN_CLOSE]: "Pick close",
} as const satisfies Record<CommandsDirectory, string>;
